import { MapPin, Calendar } from 'lucide-react';
import ScoreShield from './ScoreShield.jsx';
import SubScoreBars from './SubScoreBars.jsx';
import NarrativeSection from './NarrativeSection.jsx';
import TripMap from './TripMap.jsx';
import FactorsTable from './FactorsTable.jsx';
import DataCompleteness from './DataCompleteness.jsx';
import CrimeBackdrop from './CrimeBackdrop.jsx';
import PublicRecords from './PublicRecords.jsx';

/**
 * TripReport
 *
 * MONDAY MAY 25 UPDATE:
 *   1. Added DataCompleteness card between score and narrative
 *   2. Added CrimeBackdrop section between narrative and map (separate
 *      from contributing factors — informational only, no score impact)
 *   3. Updated map metadata line (no more incidents count)
 *
 * Section order:
 *   1. Business header
 *   2. Score + sub-scores
 *   3. Data completeness card  ← NEW
 *   4. Narrative
 *   5. Crime backdrop (all NIBRS codes)  ← NEW
 *   6. Map (places only)
 *   7. Contributing factors
 *   8. Disclaimer
 */
export default function TripReport({ report }) {
  if (!report) return null;

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return ''; }
  };

  // Coverage gap factors are already included in contributing_factors
  // by the backend orchestrator, so we don't merge them again here.
  const allFactors = report.contributing_factors || [];

  // Build map metadata summary line — places only
  const mapMeta = report.map_data ? buildMapMeta(report.map_data) : '';

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">

      {/* ============================================ */}
      {/* ZONE 1: BUSINESS HEADER */}
      {/* ============================================ */}
      <div className="card mb-6 animate-fade-in-up">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="font-heading text-2xl text-navy mb-2">
              {report.input?.business_name || 'TRIP Profile'}
            </h1>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <MapPin size={14} />
              <span>{report.input?.address}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-xs text-gray-400 uppercase tracking-wider">
              <Calendar size={12} />
              Computed
            </div>
            <div className="text-sm text-gray-600">
              {formatDate(report.metadata?.computed_at)}
            </div>
            {report.metadata?.computed_in_ms && (
              <div className="text-xs text-gray-400 mt-0.5">
                in {(report.metadata.computed_in_ms / 1000).toFixed(1)}s
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ZONE 2: THE SCORE MOMENT */}
      {/* ============================================ */}
      <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="flex-shrink-0">
            <ScoreShield score={report.score} tier={report.tier} tierColor={report.tier_color} />
          </div>
          <div className="flex-1 w-full">
            <div className="mb-4">
              <div className="text-xs text-gray-400 uppercase tracking-widest mb-1">TRIP Score</div>
              <div className="font-heading text-3xl font-black" style={{ color: report.tier_color }}>
                {report.tier_label}
              </div>
              <div className="text-sm text-gray-600 mt-1 max-w-xl">
                {report.tier_description}
              </div>
            </div>
            <SubScoreBars subScores={report.sub_scores} />
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ZONE 2.5: DATA COMPLETENESS ← NEW */}
      {/* ============================================ */}
      {report.data_completeness && (
        <DataCompleteness data={report.data_completeness} />
      )}

      {/* ============================================ */}
      {/* ZONE 3: NARRATIVE */}
      {/* ============================================ */}
      {report.narrative && (
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
          <NarrativeSection narrative={report.narrative} tierColor={report.tier_color} />
        </div>
      )}

      {/* ============================================ */}
      {/* ZONE 3.5: PUBLIC RECORDS ← NEW */}
      {/* ============================================ */}
      {report.public_records && (
        <PublicRecords data={report.public_records} />
      )}

      {/* ============================================ */}
      {/* ZONE 4: CRIME BACKDROP */}
      {/* ============================================ */}
      {report.crime_backdrop && report.crime_backdrop.available && (
        <CrimeBackdrop data={report.crime_backdrop} />
      )}

      {/* ============================================ */}
      {/* ZONE 5: MAP (places only) */}
      {/* ============================================ */}
      <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-title mb-0">Geographic context</h2>
          <div className="text-xs text-gray-500">{mapMeta}</div>
        </div>
        <TripMap mapData={report.map_data} />
      </div>

      {/* ============================================ */}
      {/* ZONE 6: CONTRIBUTING FACTORS */}
      {/* ============================================ */}
      <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.7s', animationFillMode: 'both' }}>
        <h2 className="section-title">Contributing factors</h2>
        <FactorsTable factors={allFactors} />
      </div>

      {/* ============================================ */}
      {/* ZONE 7: DISCLAIMER */}
      {/* ============================================ */}
      <div className="text-xs text-gray-400 text-center py-6 max-w-4xl mx-auto leading-relaxed">
        <strong className="text-gray-500">DISCLAIMER:</strong> This Trafficking Risk Intelligence Profile is generated
        from publicly-available data sources and scores environmental conditions at the geographic location indicated.
        It does not characterize the subject business, its operations, owners, employees, or occupants as being
        engaged in or associated with trafficking activity. TRIP output is intended as one of multiple inputs to
        professional underwriting judgment and should not be the sole basis for any underwriting, coverage, or
        business decision. All factors reference publicly-reported data with specific time windows and distances
        documented in the contributing factors detail.
      </div>
    </div>
  );
}

/**
 * Build the small metadata line that appears next to the map title.
 * Counts only PLACES (no incident counts).
 */
function buildMapMeta(mapData) {
  const parts = [];
  const f = (mapData.facilities || []).length;
  const a = (mapData.airports || []).length;
  const m = (mapData.motels || []).length;
  const fb = (mapData.flagged_businesses || []).length;
  const ts = (mapData.truck_stops || []).length;
  const fuel = (mapData.fuel_clusters || []).length;

  if (f) parts.push(`${f} facilit${f === 1 ? 'y' : 'ies'}`);
  if (m) parts.push(`${m} motel${m === 1 ? '' : 's'}`);
  if (fb) parts.push(`${fb} flagged business${fb === 1 ? '' : 'es'}`);
  if (ts) parts.push(`${ts} truck stop${ts === 1 ? '' : 's'}`);
  if (fuel) parts.push(`${fuel} indep. fuel`);
  if (a) parts.push(`${a} airport${a === 1 ? '' : 's'}`);

  return parts.length > 0 ? parts.join(' · ') : 'subject only';
}
